import React from "react";
import styled from "styled-components";
import Tooltip from "~/shared/components/Tooltip";

const StyledTag = styled.div`
  --tag-color: var(--gray-text);
  --tag-bg: rgba(64, 64, 64, 0.2);

  display: inline-block;
  padding: 1px 7px;
  background: var(--tag-bg);
  border-radius: 8px;
  color: var(--tag-color);
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
  margin-right: 3px;
  vertical-align: middle;
`;

interface TagProps extends React.HTMLAttributes<HTMLDivElement> {
  context?: string;
  children?: React.ReactNode;
}
const Tag = ({ context, children, ...rest }: TagProps) => {
  return context ? (
    <Tooltip content={context}>
      <StyledTag {...rest}>{children}</StyledTag>
    </Tooltip>
  ) : (
    <StyledTag {...rest}>{children}</StyledTag>
  );
};

const themes = {
  greenDarkText: {
    color: "var(--dark-gray)",
    bg: "var(--color-ds-green-8)",
  },
  greenWhiteText: {
    color: "var(--color-white)",
    bg: "var(--color-ds-green-9)",
  },
  redWhiteText: {
    color: "var(--dark-gray)",
    bg: "var(--color-ds-red-8)",
  },
  tier1DarkText: {
    color: "var(--dark-gray)",
    bg: "var(--color-ds-green-4)",
  },
  tier2DarkText: {
    color: "var(--dark-gray)",
    bg: "var(--color-ds-teal-4)",
  },
  tier3DarkText: {
    color: "var(--dark-gray)",
    bg: "var(--color-ds-blue-4)",
  },
  lightRedDarkText: {
    color: "var(--dark-gray)",
    bg: "var(--color-ds-red-4)",
  },
  purpleDarkText: {
    color: "var(--dark-gray)",
    bg: "var(--color-ds-purple-6)",
  },
  lightTealDarkText: {
    color: "var(--dark-gray)",
    bg: "var(--color-ds-teal-4)",
  },
  tealDarkText: {
    color: "var(--dark-gray)",
    bg: "var(--color-ds-teal-7)",
  },
  redDarkText: {
    color: "var(--dark-gray)",
    bg: "var(--color-ds-red-8)",
  },
  darkRedWhiteText: {
    color: "var(--color-white)",
    bg: "var(--color-ds-red-10)",
  },
  darkPinkWhiteText: {
    color: "var(--color-white)",
    bg: "var(--color-ds-pink-8)",
  },
  magentaWhiteText: {
    color: "var(--color-white)",
    bg: "var(--color-ds-pink-9)",
  },
  lightPinkWhiteText: {
    color: "var(--color-white)",
    bg: "var(--color-ds-pink-4)",
  },
  darkOrangeDarkText: {
    color: "var(--dark-gray)",
    bg: "var(--color-ds-orange-9)",
  },
  lightOrangeDarkText: {
    color: "var(--dark-gray)",
    bg: "var(--color-ds-orange-7)",
  },
  yellowWhiteText: {
    color: "var(--color-white)",
    bg: "var(--color-ds-yellow-9)",
  },
  yellowDarkText: {
    color: "var(--dark-gray)",
    bg: "var(--color-ds-yellow-6)",
  },
  blueDarkText: {
    color: "var(--dark-gray)",
    bg: "var(--color-ds-blue-8)",
  },
  blueWhiteText: {
    color: "var(--color-white)",
    bg: "var(--color-ds-blue-8)",
  },
  success: {
    color: "var(--success-green)",
    bg: "var(--color-ds-green-a4)",
  },
  "success-light": {
    color: "var(--color-white)",
    bg: "var(--color-ds-green-8)",
  },
  error: {
    color: "var(--error-red)",
    bg: "var(--color-ds-red-a4)",
  },
  "error-light": {
    color: "var(--color-white)",
    bg: "var(--color-ds-red-8)",
  },
  warning: {
    color: "var(--yellow-orange)",
    bg: "var(--color-ds-yellow-a4)",
  },
  "warning-light": {
    color: "var(--color-white)",
    bg: "var(--color-ds-yellow-7)",
  },
  highWarning: {
    color: "var(--brand-orange)",
    bg: "var(--color-ds-orange-a4)",
  },
  "highWarning-light": {
    color: "var(--color-white)",
    bg: "var(--color-ds-orange-7)",
  },
  general: {
    color: "var(--dark-text)",
    bg: "var(--color-ds-neutral-a4)",
  },
  "general-light": {
    color: "var(--color-white)",
    bg: "var(--color-ds-neutral-8)",
  },
  heavilyRecruited: {
    color: "var(--color-white)",
    bg: "var(--color-ds-blue-8)",
  },
  interviewing: {
    color: "var(--color-white)",
    bg: "var(--color-ds-blue-9)",
  },
  earlyInSearch: {
    color: "var(--color-white)",
    bg: "var(--color-ds-teal-9)",
  },
  apex: {
    color: "var(--color-white)",
    bg: "var(--color-ds-orange-8)",
  },
  maven: {
    color: "var(--color-white)",
    bg: "var(--color-ds-orange-10)",
  },
  veteran: {
    color: "var(--color-white)",
    bg: "var(--color-ds-yellow-9)",
  },
  transcend: {
    color: "var(--color-white)",
    bg: "var(--color-ds-orange-7)",
  },
  userNotActivated: {
    color: "var(--color-white)",
    bg: "var(--color-ds-red-11)",
  },
} as const;

type OptionsRecord = Record<string, keyof typeof themes>;

/** This type represents the case when `value` and/or `options` are defined for a given `TagController`. */
type TagValueAndOptions<TOptions extends OptionsRecord> = {
  options: TOptions;
  value: keyof TOptions;
};

/**
 * ## What's with all the generics?
 *
 * This uses a discriminated union to ensure that when either `options` or `value` is defined it will strongly type
 * check the other. If `options` or `values` are _not_ defined then it fallback to their default values.
 *
 * @example
 *   <TagController
 *     options={{ option1: "tier1DarkText", option2: "tier2DarkText" }}
 *     value="option1" // This is strongly typed and must be a key of the options object
 *   />;
 */
type TagControllerProps<TOptions extends OptionsRecord> = (
  | TagValueAndOptions<TOptions>
  | {
      options?: undefined;
      value?: never;
    }
) & {
  valueModifier?: string;
  context?: string;
  children?: React.ReactNode;
};
export const TagController = <TOptions extends OptionsRecord>({
  value,
  options,
  valueModifier = "",
  context = "",
  children,
}: TagControllerProps<TOptions>) => {
  let theme: Readonly<{ color: string; bg: string }> = themes.general;

  if (options && value && options[value]) {
    theme = themes[options[value]];
  }

  return (
    <Tag
      context={context}
      style={
        {
          "--tag-color": theme?.color,
          "--tag-bg": theme?.bg,
        } as React.CSSProperties
      }
    >
      {value != null && typeof value !== "symbol" && value}
      {valueModifier ? `- ${valueModifier}` : null}
      {children}
    </Tag>
  );
};
